/**
 * useTranscriptStream — TV-P7-05 (2026-06-02)
 *
 * Subscribes to the live STT transcript for a given encounter.
 * Backs the AIDocs.jsx route (full-page transcript) + the
 * compact AINotesTab preview inside PatientPanel.jsx.
 *
 * Data shape (mirrors what televisit-stt-ingest writes — see
 * televisit-stt-ingest/src/server.ts):
 *
 *   televisit-transcripts/{encounterId}            ← metadata doc
 *     { status: 'streaming' | 'ended',
 *       startedAt, lastTrackAt }
 *
 *   televisit-transcripts/{encounterId}/chunks/{auto}
 *     { text, isFinal, confidence, languageCode,
 *       participantIdentity, trackId,
 *       receivedAt (server), clientTimestamp }
 *
 * The hook returns:
 *   - `chunks`           — array of chunk docs ordered by
 *                          receivedAt ASC (oldest first)
 *   - `metadata`         — the encounter-level metadata doc
 *   - `isStreaming`      — true while STT is actively producing
 *   - `error`            — Firestore error if the subscribe blew up
 *
 * Default cap of 500 chunks (configurable via opts.limit) so a
 * marathon visit doesn't fill the renderer with thousands of
 * interim results. AIDocs displays interims as faded text and
 * collapses them as soon as the matching final lands.
 */

import { useEffect, useState } from 'react';
import {
  collection,
  doc,
  limitToLast,
  onSnapshot,
  orderBy,
  query,
} from 'firebase/firestore';
import { firestore } from './config';

const DEFAULT_LIMIT = 500;

export function useTranscriptStream(encounterId, opts = {}) {
  const { limit = DEFAULT_LIMIT } = opts;
  const [chunks, setChunks] = useState([]);
  const [metadata, setMetadata] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!encounterId) {
      setChunks([]);
      setMetadata(null);
      return undefined;
    }

    setError(null);

    // ── metadata doc ───────────────────────────────────────────
    const metaRef = doc(firestore, 'televisit-transcripts', encounterId);
    const unsubMeta = onSnapshot(
      metaRef,
      (snap) => {
        setMetadata(snap.exists() ? { id: snap.id, ...snap.data() } : null);
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn(
          '[useTranscriptStream] metadata subscribe error:',
          err.message,
        );
        setError(err);
      },
    );

    // ── chunks subcollection ───────────────────────────────────
    // Order by receivedAt (serverTimestamp) so cross-track ordering
    // is consistent. limitToLast keeps the renderer fast even on
    // long visits — older chunks scroll off the top of the UI.
    const chunksRef = collection(
      firestore,
      'televisit-transcripts',
      encounterId,
      'chunks',
    );
    const q = query(chunksRef, orderBy('receivedAt', 'asc'), limitToLast(limit));
    const unsubChunks = onSnapshot(
      q,
      (snap) => {
        setChunks(
          snap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          })),
        );
      },
      (err) => {
        // eslint-disable-next-line no-console
        console.warn(
          '[useTranscriptStream] chunks subscribe error:',
          err.message,
        );
        setError(err);
      },
    );

    return () => {
      unsubMeta();
      unsubChunks();
    };
  }, [encounterId, limit]);

  return {
    chunks,
    metadata,
    isStreaming: metadata?.status === 'streaming',
    error,
  };
}
